/* ============================================================================
   <featured-products-slider>
   - Click prev/next → smooth-scroll the track by N cards
   - Disable prev/next when at scroll start / end
   - Variant swatches → swap the card's primary image
   ============================================================================ */
(function () {
  'use strict';

  if (customElements.get('featured-products-slider')) return;

  class FeaturedProductsSlider extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.scrollFrame = null;
    }

    connectedCallback() {
      this.track = this.querySelector('.cps__track');
      if (!this.track) return;

      this.prevBtn = document.getElementById(this.dataset.prevId);
      this.nextBtn = document.getElementById(this.dataset.nextId);

      this.stepDesktop = parseInt(this.dataset.stepDesktop, 10) || 1;
      this.stepMobile = parseInt(this.dataset.stepMobile, 10) || 2;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.slide(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.slide(1));
      }

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.bindSwatches();

      // Initial state — wait one frame so layout is final.
      requestAnimationFrame(() => this.updateButtons());
    }

    disconnectedCallback() {
      this.track && this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    /* --- Slider navigation ---------------------------------------------- */
    slide(direction) {
      const slide = this.track.querySelector('.cps__slide');
      if (!slide) return;

      const slideWidth = slide.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0) || 0;
      const stepCount = window.matchMedia('(min-width: 990px)').matches
        ? this.stepDesktop
        : this.stepMobile;

      const distance = (slideWidth + gap) * stepCount * direction;
      this.track.scrollBy({ left: distance, behavior: 'smooth' });
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateButtons());
    }

    onResize() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateButtons());
    }

    updateButtons() {
      if (!this.prevBtn || !this.nextBtn) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      const atStart = left <= 1;
      const atEnd = left >= max - 1;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;

      // If everything fits with no overflow, hide both arrows entirely.
      const noOverflow = max <= 1;
      this.prevBtn.toggleAttribute('hidden', noOverflow);
      this.nextBtn.toggleAttribute('hidden', noOverflow);
    }

    /* --- Variant swatches: swap card image on hover/focus/click --------- */
    bindSwatches() {
      this.querySelectorAll('.cps-card').forEach((card) => {
        const primary = card.querySelector('.cps-card__img--primary');
        const swatches = card.querySelectorAll('[data-cps-swatch]');
        if (!primary || swatches.length === 0) return;

        // Stash original image so we can restore it on mouseleave.
        const originalSrc = primary.getAttribute('src');
        const originalSrcset = primary.getAttribute('srcset');

        const apply = (swatch) => {
          const newSrc = swatch.dataset.src;
          const newSrcset = swatch.dataset.srcset;
          if (newSrc) primary.setAttribute('src', newSrc);
          if (newSrcset) primary.setAttribute('srcset', newSrcset);
          swatches.forEach((s) => s.classList.toggle('cps-card__swatch--active', s === swatch));
        };

        const restore = () => {
          if (originalSrc) primary.setAttribute('src', originalSrc);
          if (originalSrcset) primary.setAttribute('srcset', originalSrcset);
          else primary.removeAttribute('srcset');
          swatches.forEach((s) => s.classList.remove('cps-card__swatch--active'));
        };

        swatches.forEach((swatch) => {
          // hover → preview
          swatch.addEventListener('mouseenter', () => apply(swatch));
          swatch.addEventListener('focus', () => apply(swatch));

          // click → keep the variant image as the new "current" (don't restore on leave)
          swatch.addEventListener('click', () => {
            apply(swatch);
            // Mark this swatch as the new baseline so leaving doesn't snap back.
            swatch.dataset.cpsSticky = 'true';
          });
        });

        // Restore when cursor leaves the swatch list, *unless* a sticky swatch was clicked.
        const list = card.querySelector('.cps-card__swatches');
        if (list) {
          list.addEventListener('mouseleave', () => {
            const stuck = card.querySelector('[data-cps-sticky="true"]');
            if (!stuck) restore();
          });
        }
      });
    }
  }

  customElements.define('featured-products-slider', FeaturedProductsSlider);
})();
